/**
 * DeviceList Component
 * 
 * Main component for viewing and managing registered physical devices.
 * Displays a list of all registered device instances with the ability to:
 * - View all registered devices
 * - See device details (ID, MAC address, status, etc.)
 * - Edit device configurations
 * - Delete device registrations
 * 
 * The component uses Firestore real-time listener to keep the device list
 * synchronized with the database.
 * 
 * Layout: Responsive flex-wrap grid that automatically adjusts based on screen width
 * - Desktop: 2-column layout
 * - Tablet/Mobile: Single column layout
 * 
 * Access: Owner role only
 */
import React, { useEffect, useState } from "react";

import {
  onSnapshot,
  collection,
  query,
} from "firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Typography, Alert } from "@mui/material";

import { firebaseStore } from "../../../components/firebase/firestore";
import CardsDeviceList from "./components/CardsDeviceList";

import "./DeviceList.css";

function DeviceList() {
  // State for storing the array of registered devices from Firestore
  const [devices, setDevices] = useState<any[]>([]);
  
  // Loading state for initial data fetch
  const [loading, setLoading] = useState(true);

  /**
   * Set up Firestore real-time listener for devices collection
   * Updates component state whenever data changes in Firestore
   * Automatically unsubscribes on component unmount
   */
  useEffect(() => {
    const q = query(collection(firebaseStore, "devices"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempDevices: any[] = [];
      querySnapshot.forEach((doc) => {
        // Combine document data with its ID for reference
        tempDevices.push({...doc.data(), id: doc.id});
      });
      // Sort devices by registration date (newest first)
      tempDevices.sort((a, b) => b.registeredAt - a.registeredAt);
      setDevices(tempDevices);
      setLoading(false);
    });
    
    // Cleanup: Unsubscribe from listener when component unmounts
    return unsubscribe;
  }, []);

  return (
    <div className="DeviceList">
      {/* Header Section */}
      <Box sx={{ maxWidth: 1400, margin: "0 auto", padding: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Registered Devices
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ marginBottom: 3 }}>
          View and manage all registered physical device instances
        </Typography>
      </Box>
      
      {/* Loading State: Show spinner while fetching data */}
      {loading ? (
        <div className="DeviceListLoading">
          <CircularProgress sx={{ color: "#44a99a" }} />
        </div>
      ) : devices.length === 0 ? (
        /* Empty State: No devices registered */
        <Box sx={{ maxWidth: 1400, margin: "0 auto", padding: 3 }}>
          <Alert severity="info">
            No devices registered yet. Go to Register Device to add your first device.
          </Alert>
        </Box>
      ) : (
        /* Devices Grid: Responsive layout that adapts to screen size */
        <div className="DeviceListGrid">
          {devices.map((device) => (
            <CardsDeviceList
              key={device.id}
              device={device}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DeviceList;
